import React from "react";
import styled, { keyframes } from "styled-components";

type LoaderDSCProps = {
  /** Escala visual do loader (1 = 225x150px) */
  scale?: number;
  /** Multiplicador da velocidade (1 = 6s do original; 2 = duas vezes mais rápido) */
  speed?: number;
  /** Cores opcionais (padrão = paleta Google) */
  colors?: {
    red?: string;
    blue?: string;
    yellow?: string;
    green?: string;
  };
  className?: string;
  "aria-label"?: string;
};

const dscDot1 = keyframes`
  0%,100%{opacity:0;transform:scale(0)}
  5%,95%{opacity:1;transform:scale(1)}
  25%,75%{width:40px}
  30%,70%{width:100px;transform:rotate(0)}
  35%,65%{transform:rotate(-30deg)}
  45%,55%{transform:rotate(30deg)}
  50%{width:100px;transform:rotate(30deg)}
`;
const dscDot2 = keyframes`
  0%,100%{opacity:0;transform:scale(0)}
  5%,95%{opacity:0;transform:scale(0)}
  10%,90%{opacity:1;width:40px;transform:scale(1)}
  15%,85%{left:62.5px}
  20%,80%{width:100px;left:125px;transform:scale(1);transform-origin:80px center}
  25%,75%{transform:scale(0)}
  30%,70%{transform:scale(0);background-color:var(--color-red)}
  36%,64%{background-color:var(--color-yellow)}
  40%,60%{width:100px;left:125px;transform:scale(1) rotate(-30deg)}
  50%{width:100px;left:125px;background-color:var(--color-yellow);transform:scale(1) rotate(-30deg);transform-origin:80px center}
`;
const dscDot3 = keyframes`
  0%,100%{opacity:0;transform:scale(0)}
  10%,90%{opacity:0;transform:scale(0)}
  15%,85%{opacity:1;transform:scale(1);width:40px}
  20%,80%{width:100px;left:62.5px}
  25%,75%{width:40px;left:122.5px;transform:scale(1)}
  30%,70%{transform:scale(0)}
  50%{transform:scale(0)}
`;
const dscDot4 = keyframes`
  0%,100%{opacity:0;transform:scale(0)}
  15%,85%{opacity:0;transform:scale(0)}
  20%,80%{opacity:1;transform:scale(1);width:40px}
  25%,75%{width:100px;left:62.5px}
  30%,70%{width:40px;left:122.5px;transform:scale(1)}
  35%,65%{transform:scale(0)}
  50%{transform:scale(0)}
`;
const dscDot5 = keyframes`
  0%,100%{opacity:0;transform:scale(0);transform-origin:left center}
  10%,90%{opacity:0;transform:scale(0)}
  15%,85%{opacity:1;transform:scale(1);width:40px;left:62.5px;transform-origin:left center}
  20%,80%{width:100px;left:125px;transform-origin:80px center}
  30%,70%{transform:rotate(0)}
  35%,65%{transform:rotate(-30deg)}
  45%,55%{transform:rotate(30deg)}
  50%{opacity:1;transform:scale(1) rotate(30deg);transform-origin:80px center;left:125px;width:100px}
`;
const dscDot6 = keyframes`
  0%,100%{opacity:0;transform:scale(0)}
  15%,85%{opacity:0;transform:scale(0)}
  20%,80%{opacity:1;transform:scale(1);width:40px}
  22.5%,77.5%{width:100px;left:62.5px}
  27.5%,72.5%{width:40px;left:122.5px;transform:scale(1)}
  32.5%,67.5%{transform:scale(0)}
  50%{transform:scale(0)}
`;
const dscDot7 = keyframes`
  0%,100%{opacity:0;transform:scale(0)}
  35%,65%{opacity:1;transform:scale(0)}
  36%,64%{transform:scale(1);width:100px;transform:rotate(-30deg)}
  50%{transform:scale(1);width:100px;transform:rotate(-30deg)}
`;

const Wrapper = styled.div<Required<Pick<LoaderDSCProps, "scale" | "speed">> & { $colors: Required<NonNullable<LoaderDSCProps["colors"]>> }>`
  /* Variáveis de cor */
  --color-red: ${({ $colors }) => $colors.red};
  --color-blue: ${({ $colors }) => $colors.blue};
  --color-yellow: ${({ $colors }) => $colors.yellow};
  --color-green: ${({ $colors }) => $colors.green};

  position: relative;
  width: 225px;
  height: 150px;

  /* Escala visual sem recalcular posições */
  transform: ${({ scale }) => `scale(${scale})`};
  transform-origin: top left;

  /* Controle de velocidade: 6s / speed */
  --dur: ${({ speed }) => `${6 / speed}s`};
`;

const DotBase = styled.div`
  position: absolute;
  width: 40px;
  height: 40px;
  border-radius: 9999px;
`;

/** Dot 1 */
const Dot1 = styled(DotBase)`
  top: 55px;
  background-color: var(--color-blue);
  transform-origin: 20px center;
  animation: ${dscDot1} var(--dur) infinite ease-in-out;
  z-index: 2;
`;
/** Dot 2 */
const Dot2 = styled(DotBase)`
  left: 62.5px;
  top: 55px;
  background-color: var(--color-red);
  animation: ${dscDot2} var(--dur) infinite ease-in-out;
`;
/** Dot 3 */
const Dot3 = styled(DotBase)`
  bottom: 0;
  left: 62.5px;
  background-color: var(--color-green);
  animation: ${dscDot3} var(--dur) infinite ease-in-out;
`;
/** Dot 4 */
const Dot4 = styled(DotBase)`
  left: 62.5px;
  background-color: var(--color-green);
  animation: ${dscDot4} var(--dur) infinite ease-in-out;
`;
/** Dot 5 */
const Dot5 = styled(DotBase)`
  left: 62.5px;
  top: 55px;
  background-color: var(--color-green);
  animation: ${dscDot5} var(--dur) infinite ease-in-out;
`;
/** Dot 6 */
const Dot6 = styled(DotBase)`
  left: 62.5px;
  bottom: 0;
  background-color: var(--color-yellow);
  animation: ${dscDot6} var(--dur) infinite ease-in-out;
`;
/** Dot 7 */
const Dot7 = styled(DotBase)`
  top: 55px;
  background-color: var(--color-red);
  transform-origin: 20px center;
  animation: ${dscDot7} var(--dur) infinite ease-in-out;
  z-index: 1;
`;

export const LoaderDSC: React.FC<LoaderDSCProps> = ({
  scale = 1,
  speed = 1,
  colors,
  className,
  "aria-label": ariaLabel = "Carregando",
}) => {
  const palette = {
    red: colors?.red ?? "#D93025",
    blue: colors?.blue ?? "#1264C7",
    yellow: colors?.yellow ?? "#fbbc05",
    green: colors?.green ?? "#1E8E3E",
  };

  return (
    <Wrapper
      role="status"
      aria-label={ariaLabel}
      className={className}
      scale={scale}
      speed={speed}
      $colors={palette}
    >
      <Dot1 />
      <Dot2 />
      <Dot3 />
      <Dot4 />
      <Dot5 />
      <Dot6 />
      <Dot7 />
    </Wrapper>
  );
};
